import { Dictionary } from "../store/models/common_types";
import { formatMCapAndEVCell } from "../utils/common";
import { formatRevSupCell } from "../utils/comparison-grid";
import {
  EPS,
  PRICE_PERFORMANCE,
  PRICE_TARGET_RETURNS,
  RATINGS,
  TOTAL_REVENUE_PRIMARY,
} from "./tpr-constants";
import { UserTprSettingObject } from "./tpr.model";
export function getRatingBadgeDetails(rating) {
  switch (rating) {
    case "Buy":
      return {
        bgColor: "#006800",
        textColor: "#fff",
        text: "BUY",
        id: 1,
      };
    case "Hold":
      return {
        bgColor: "#FFC846",
        textColor: "#16181C",
        text: "HOLD",
        id: 2,
      };
    case "Sell":
      return {
        bgColor: "#B40000",
        textColor: "#fff",
        text: "SELL",
        id: 3,
      };
    case "Not Rated":
      return {
        bgColor: undefined,
        textColor: "#16181C",
        text: "Not Rated",
        id: 0,
      };
    default:
      return {
        bgColor: undefined,
        textColor: undefined,
        text: undefined,
      };
  }
}

export function getPositiveNegativeTextColor(number) {
  if (number > 0) {
    return "#008300";
  }
  if (number < 0) {
    return "#B40000";
  }
  return "#16181C";
}

export function updateColumnPickerData(columnPickerData, selectedItems) {
  let indexCount = (selectedItems.length || 0) - 1;
  const result = [];
  for (const item of columnPickerData) {
    const indexOfSelectedItem = selectedItems.indexOf(item.id);
    const formattedItem = {
      ...item,
      isSelected: indexOfSelectedItem !== -1,
      index: indexOfSelectedItem !== -1 ? indexOfSelectedItem : indexCount++,
    };
    result.push(formattedItem);
  }
  return result;
}

/**
 * This method id to get the array of company id from tickers
 * @param {array} tickerInfo - ticker_info array that we get from meta data
 * @returns {array} array of company id
 */
export function getTickersCompanyId(tickerInfo) {
  return tickerInfo.map((item) => item.companyid || item.cid);
}

/**
 * This method is use to set the userSetting details, which we can use for creating request data for userSetting api
 * @param {string} tabType - type of tab [peer | company]
 * @param {string} currentTicker - Id of main ticker
 * @param {object} ratios - ratio object state
 * @param {object} periods - period object state
 * @param {string} currency - selected currency
 *  @param {array} tickers - ticker_info array that we get from meta data
 * @returns {UserTprSettingObject} user setting details
 */
export function getUserTprSettingDetails(obj: {
  tabType;
  currentTicker;
  tickers: Array<any>;
  currency?;
  pricePerformance?;
  orientation?;
}): UserTprSettingObject {
  const userSettingDetails: UserTprSettingObject = {
    tabType: obj.tabType,
    currentTicker: obj.currentTicker,
    tickers: getTickersCompanyId(obj.tickers),
    currency: obj.currency,
    pricePerformance: obj.pricePerformance,
    orientation: obj.orientation,
  };
  return userSettingDetails;
}

export type TabType = "peer" | "company";

export function getTickersId(arr = []) {
  return arr.map((item) => item.ticker);
}

/**
 * This method is use to update the already created userSetting details.
 * We can pass the property and their value that we want to update
 * @param {UserTprSettingObject} userSettingDetails - userSetting object which we needs to update
 * @param {object} objToUpdate - object with property and their value, which we want to update
 * @returns {UserTprSettingObject} updated user setting details
 */
export function updateUserTprSettingDetails(
  userTprSettingDetails: UserTprSettingObject,
  objToUpdate: {
    tabType?: TabType;
    currentTicker?: string;
    tickers?: Array<number>;
    currency?: string;
    orientation?: string;
    pricePerformance?: string;
  },
): UserTprSettingObject {
  const updatedUserTprSettingDetails = {
    ...userTprSettingDetails,
    ...objToUpdate,
  };
  return updatedUserTprSettingDetails;
}

export function computeVsPeersGridAverage(gridData, rawData): Dictionary<any> {
  let _averages = {};
  const currency = gridData.currency;
  // average calculation for extra columns
  const extraColumns = {
    C2: {
      formatted: "-",
      raw: undefined,
    },
    C3: {
      formatted: "-",
      raw: undefined,
    },
  };
  const extraColumnsKeys = Object.keys(extraColumns);
  const row = Object.keys(rawData);
  if (currency === "Base") {
    _averages = { ...extraColumns };
  } else {
    for (let l = 0; l < extraColumnsKeys.length; l++) {
      const item = extraColumnsKeys[l];
      let currencySymbol = "";
      let count = 0;
      let sum = 0;
      for (let k = 0; k < row.length; k++) {
        const _row = rawData[row[k]];
        if (_row[item]["raw"] !== undefined && _row[item]["raw"] !== "") {
          count++;
          sum = sum + Number(_row[item]["raw"]);
          if (_row[item]["currency"]) {
            currencySymbol = _row[item]["currency"];
          }
        }
      }
      if (count === 0) {
        _averages[item] = {
          formatted: "-",
          raw: undefined,
        };
      } else {
        const avg = sum / count;
        _averages[item] = {
          formatted: formatMCapAndEVCell(avg, currencySymbol),
          raw: avg,
        };
      }
    }
  }

  //2nd Half
  for (let i = 4; i < gridData.columns.length; i++) {
    let unit = "";
    const colHeader = gridData.columns[i];
    const label = gridData.colInfo[colHeader].label;
    if (label === RATINGS) continue;

    if (gridData.colInfo[colHeader].subheaders_info) {
      let subColumns = gridData.colInfo[colHeader].subheaders_info.map(
        (value) => value.id,
      );
      if (label === PRICE_TARGET_RETURNS) {
        subColumns = subColumns.filter(
          (ids) => ids !== "target_price" && ids !== 10,
        );
      }

      if (label === PRICE_PERFORMANCE || label === PRICE_TARGET_RETURNS) {
        unit = "%";
      }

      const subColumnAverage: any = {};
      for (const subCol of subColumns) {
        if (
          (label === EPS || label === TOTAL_REVENUE_PRIMARY) &&
          currency === "Base"
        ) {
          subColumnAverage[subCol] = {
            formatted: "-",
            raw: undefined,
          };
          continue;
        }
        let currencySymbol = "";
        let count = 0;
        let sum = 0;
        for (let k = 0; k < row.length; k++) {
          const _row = rawData[row[k]];
          if (
            _row[colHeader].subColumns[subCol] &&
            typeof _row[colHeader].subColumns[subCol]["raw"] === "number"
          ) {
            const _subColumn = _row[colHeader].subColumns[subCol];
            count++;
            sum = sum + Number(_subColumn["raw"]);
            if (_subColumn["currency"] && label !== PRICE_PERFORMANCE) {
              currencySymbol = _subColumn["currency"];
            }
          }
        }
        if (count === 0) {
          subColumnAverage[subCol] = {
            formatted: "-",
            raw: undefined,
          };
        } else {
          const avg = sum / count;
          subColumnAverage[subCol] = {
            formatted: formatRevSupCell(avg, currencySymbol, unit),
            raw: avg,
          };
        }
      }
      _averages[colHeader] = subColumnAverage;
    }
  }
  return _averages;
}

export function computeVsPeersGridMedian(gridData, rawData): Dictionary<any> {
  let _medians = {};
  const currency = gridData.currency;

  // median calculation for extra columns
  const extraColumns = {
    C2: {
      formatted: "-",
      raw: undefined,
    },
    C3: {
      formatted: "-",
      raw: undefined,
    },
  };
  const extraColumnsKeys = Object.keys(extraColumns);
  const row = Object.keys(rawData);
  if (currency === "Base") {
    _medians = { ...extraColumns };
  } else {
    for (let l = 0; l < extraColumnsKeys.length; l++) {
      let currencySymbol = "";
      const item = extraColumnsKeys[l];
      const _vals = [];
      for (let k = 0; k < row.length; k++) {
        const _row = rawData[row[k]];
        if (_row[item]["raw"] !== undefined && _row[item]["raw"] !== "") {
          _vals.push(Number(_row[item]["raw"]));
          if (_row[item]["currency"]) {
            currencySymbol = _row[item]["currency"];
          }
        }
      }
      if (_vals.length === 0) {
        _medians[item] = {
          formatted: "-",
          raw: undefined,
        };
      } else {
        const sortedValues = _vals.sort((a, b) => a - b);
        const lowMiddle = Math.floor((sortedValues.length - 1) / 2);
        const highMiddle = Math.ceil((sortedValues.length - 1) / 2);
        const median = (sortedValues[lowMiddle] + sortedValues[highMiddle]) / 2;
        _medians[item] = {
          formatted: formatMCapAndEVCell(median, currencySymbol),
          raw: median,
        };
      }
    }
  }

  for (let i = 4; i < gridData.columns.length; i++) {
    let unit = "";
    const colHeader = gridData.columns[i];
    const label = gridData.colInfo[colHeader].label;
    if (label === RATINGS) continue;

    if (gridData.colInfo[colHeader].subheaders_info) {
      let subColumns = gridData.colInfo[colHeader].subheaders_info.map(
        (value) => value.id,
      );
      if (label === PRICE_TARGET_RETURNS) {
        subColumns = subColumns.filter(
          (ids) => ids !== "target_price" && ids !== 10,
        );
      }

      if (label === PRICE_PERFORMANCE || label === PRICE_TARGET_RETURNS) {
        unit = "%";
      }

      const subColumnMedian: any = {};
      for (const subCol of subColumns) {
        if (
          (label === EPS || label === TOTAL_REVENUE_PRIMARY) &&
          currency === "Base"
        ) {
          subColumnMedian[subCol] = {
            formatted: "-",
            raw: undefined,
          };
          continue;
        }
        const _vals = [];
        let currencySymbol = "";
        for (let k = 0; k < row.length; k++) {
          const _row = rawData[row[k]];
          if (
            _row[colHeader].subColumns[subCol] &&
            typeof _row[colHeader].subColumns[subCol]["raw"] === "number"
          ) {
            const _subColumn = _row[colHeader].subColumns[subCol];
            _vals.push(Number(_subColumn["raw"]));
            if (_subColumn["currency"] && label !== PRICE_PERFORMANCE) {
              currencySymbol = _subColumn["currency"];
            }
          }
        }
        if (_vals.length === 0) {
          subColumnMedian[subCol] = {
            formatted: "-",
            raw: undefined,
          };
        } else {
          const sortedValues = _vals.sort((a, b) => a - b);
          const lowMiddle = Math.floor((sortedValues.length - 1) / 2);
          const highMiddle = Math.ceil((sortedValues.length - 1) / 2);
          const median =
            (sortedValues[lowMiddle] + sortedValues[highMiddle]) / 2;
          subColumnMedian[subCol] = {
            formatted: formatRevSupCell(median, currencySymbol, unit),
            raw: median,
          };
        }
      }
      _medians[colHeader] = subColumnMedian;
    }
  }

  return _medians;
}

export function getAddedAndRemovedColumnPickerItemsLabels(
  originalItems,
  selectedItems,
) {
  const addedItems = [];
  const removedItems = [];
  for (const item of originalItems) {
    let existsInselectedItems = false;
    for (const otherItem of selectedItems) {
      if (item.id === otherItem.id) {
        if (!item.isSelected && otherItem.isSelected) {
          addedItems.push(item.name);
        }
        existsInselectedItems = true;
        break;
      }
    }
    if (!existsInselectedItems) {
      removedItems.push(item.name);
    }
  }
  return { addedItems, removedItems };
}

export function getAddedAndRemovedItems(originalItems, selectedItems) {
  const addedItems = selectedItems.filter(
    (item) => originalItems.indexOf(item) < 0,
  );
  const removedItems = originalItems.filter(
    (item) => selectedItems.indexOf(item) < 0,
  );
  return { addedItems, removedItems };
}
